import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogIcon,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { CircleNotch, CalendarDots } from "@phosphor-icons/react";
import { formatDateWithOptionsBs } from "@/lib/format";
import { formatTimeBs, todayInZone, utcToWallClock } from "@/lib/timezone";

interface RescheduleDialogProps {
  /** `durationMinutes` is the session's snapshot length; the grid only offers starts where it fits. */
  session: { id: string; scheduledAt: string; therapistId: string; durationMinutes?: number };
  onClose: () => void;
  onConfirm: (data: { id: string; date: string; time: string }) => void;
  isPending: boolean;
}

// A Sarajevo calendar date (YYYY-MM-DD) as a local-midnight Date. The day
// picker and every comparison below read local components only, so this keeps
// them on the Sarajevo calendar for a browser in any timezone.
export function calendarDateAsLocal(ymd: string): Date {
  const [y, m, d] = ymd.split("-").map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
}

function toYmd(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/** Days before today in Sarajevo can't be picked as the new date. */
export function isBeforeSarajevoToday(date: Date, todayYmd: string = todayInZone()): boolean {
  return toYmd(date) < todayYmd;
}

export function RescheduleDialog({ session, onClose, onConfirm, isPending }: RescheduleDialogProps) {
  // The session's own day on the Sarajevo calendar, not the browser's.
  const oldDate = useMemo(() => calendarDateAsLocal(utcToWallClock(session.scheduledAt).date), [session.scheduledAt]);
  const todayYmd = useMemo(() => todayInZone(), []);
  const [rescheduleDate, setRescheduleDate] = useState<Date | undefined>(oldDate);
  const [rescheduleSelectedTime, setRescheduleSelectedTime] = useState<string | null>(null);

  const myTherapistId = session.therapistId;

  // Schedule overview for reschedule calendar
  const { data: scheduleOverview } = useQuery({
    queryKey: ["schedule-overview", myTherapistId],
    queryFn: async () => {
      const res = await fetch(`/api/therapists/${myTherapistId}/schedule-overview`);
      if (!res.ok) return { availableDays: [], blockedDates: [], addedDates: [] };
      return res.json() as Promise<{ availableDays: number[]; blockedDates: string[]; addedDates: string[] }>;
    },
    enabled: !!myTherapistId,
  });

  const availableDaysSet = useMemo(() => new Set(scheduleOverview?.availableDays ?? []), [scheduleOverview]);
  const blockedDatesSet = useMemo(() => new Set(scheduleOverview?.blockedDates ?? []), [scheduleOverview]);
  const addedDatesSet = useMemo(() => new Set(scheduleOverview?.addedDates ?? []), [scheduleOverview]);

  const isDateAvailable = (date: Date) => {
    const dow = date.getDay();
    const ds = toYmd(date);
    if (blockedDatesSet.has(ds)) return false;
    if (addedDatesSet.has(ds)) return true;
    return availableDaysSet.has(dow);
  };

  const rescheduleDateStr = rescheduleDate ? toYmd(rescheduleDate) : null;

  const { data: rescheduleSlotsData, isLoading: rescheduleSlotsLoading } = useQuery({
    // "availableOnly" namespaces this from the includeBooked grid used by the
    // booking wizard / AddSessionDialog. Sharing the key let their booked-flagged
    // slots leak in here and render as selectable.
    queryKey: ["available-slots", myTherapistId, rescheduleDateStr, "availableOnly", session.durationMinutes ?? null],
    queryFn: async () => {
      // reschedule.ts validates the session's own duration, not the grid step.
      const params = new URLSearchParams({ date: rescheduleDateStr! });
      if (session.durationMinutes) params.set("durationMinutes", String(session.durationMinutes));
      const res = await fetch(`/api/therapists/${myTherapistId}/available-slots?${params}`);
      if (!res.ok) return { slots: [] };
      return res.json() as Promise<{ slots: Array<{ time: string; endTime: string; booked?: boolean }> }>;
    },
    enabled: !!myTherapistId && !!rescheduleDateStr,
  });

  // Defensive: the available-only endpoint never returns booked slots, but guard
  // anyway so a booked slot can never render as selectable here.
  const rescheduleSlots = (rescheduleSlotsData?.slots ?? []).filter((s) => s.booked !== true);

  // Display in Europe/Sarajevo wall-clock, not browser-local — `session.scheduledAt`
  // is UTC ISO with Z suffix, and we want a consistent rendering across user TZs.
  const oldFormatted = formatDateWithOptionsBs(session.scheduledAt, {
    weekday: "short",
    day: "numeric",
    month: "long",
    timeZone: "Europe/Sarajevo",
  });
  const oldTime = formatTimeBs(session.scheduledAt);
  const newFormatted = rescheduleDate
    ? formatDateWithOptionsBs(rescheduleDate, { weekday: "short", day: "numeric", month: "long" })
    : null;

  const calendarDisabled = (date: Date) => {
    if (isBeforeSarajevoToday(date, todayYmd)) return true;
    return !isDateAvailable(date);
  };

  return (
    <Dialog open onClose={onClose} className="sm:max-w-lg">
      <DialogHeader onClose={onClose}>
        <div className="flex items-center gap-3">
          <DialogIcon>
            <CalendarDots className="size-5" />
          </DialogIcon>
          <div>
            <DialogTitle>Premjesti sesiju</DialogTitle>
            <DialogDescription className="mt-0 text-xs">
              Klijent će biti obaviješten putem emaila
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <DialogBody className="space-y-5">
        {/* Old -> New comparison */}
        <div className="flex items-center gap-3 rounded-xl p-3" style={{ background: "oklch(97% 0.01 84)" }}>
          <div className="flex-1 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Trenutni termin</p>
            <p className="mt-1 text-sm font-medium text-foreground">{oldFormatted}</p>
            <p className="text-xs text-muted-foreground">{oldTime}</p>
          </div>
          <div className="text-muted-foreground/40">&rarr;</div>
          <div className="flex-1 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Novi termin</p>
            {rescheduleSelectedTime ? (
              <>
                <p className="mt-1 text-sm font-medium text-primary">{newFormatted}</p>
                <p className="text-xs text-primary">{rescheduleSelectedTime}</p>
              </>
            ) : rescheduleDate ? (
              <>
                <p className="mt-1 text-sm font-medium text-primary">{newFormatted}</p>
                <p className="text-xs text-muted-foreground/40">Odaberite vrijeme</p>
              </>
            ) : (
              <p className="mt-1 text-sm text-muted-foreground/40">Odaberite datum</p>
            )}
          </div>
        </div>

        {/* Calendar */}
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Datum</p>
          <Calendar
            mode="single"
            defaultMonth={oldDate}
            selected={rescheduleDate}
            onSelect={(d) => { setRescheduleDate(d); setRescheduleSelectedTime(null); }}
            disabled={calendarDisabled}
          />
        </div>

        {/* Time slots */}
        {rescheduleDate && (
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Vrijeme</p>
            {rescheduleSlotsLoading ? (
              <div className="flex items-center justify-center py-6">
                <CircleNotch className="size-5 animate-spin text-primary/40" />
              </div>
            ) : rescheduleSlots.length === 0 ? (
              <p className="rounded-xl bg-muted/20 py-4 text-center text-sm text-muted-foreground">
                Nema dostupnih termina za ovaj datum.
              </p>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {rescheduleSlots.map((slot) => (
                  <button
                    key={slot.time}
                    onClick={() => setRescheduleSelectedTime(slot.time)}
                    className={cn(
                      "rounded-xl border py-2 text-sm font-semibold transition-all",
                      rescheduleSelectedTime === slot.time
                        ? "border-primary bg-primary text-primary-foreground shadow-md"
                        : "border-border/60 text-foreground hover:border-primary/40 hover:bg-primary/5"
                    )}
                  >
                    {slot.time}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogBody>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Otkaži
        </Button>
        <Button
          onClick={() => {
            if (!rescheduleDate || !rescheduleSelectedTime) return;
            onConfirm({
              id: session.id,
              date: rescheduleDateStr!,
              time: rescheduleSelectedTime,
            });
          }}
          disabled={isPending || !rescheduleDate || !rescheduleSelectedTime}
        >
          {isPending ? <CircleNotch className="size-4 animate-spin" /> : <CalendarDots className="size-4" />}
          Potvrdi premještanje
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
